import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { I18nService } from '../../core/i18n.service';
import { PageSection } from '../page-section/page-section';

@Component({ selector: 'app-signup', standalone: true, imports: [FormsModule, RouterLink, PageSection], templateUrl: './signup.html', styleUrl: './signup.scss' })
export class Signup {
  username = '';
  firstName = '';
  lastName = '';
  email = '';
  password = '';
  verificationCode = '';
  status: 'idle' | 'invalid' | 'invalidUsername' | 'weakPassword' | 'pending' | 'confirming' | 'confirmed' | 'error' | 'confirmationError' = 'idle';
  isSubmitting = false;
  isConfirming = false;

  constructor(public i18n: I18nService, private api: ApiService, private router: Router) {}

  async createAccount(): Promise<void> {
    if (this.isSubmitting) return;

    const username = this.username.trim().toLowerCase();
    if (!username || !this.firstName.trim() || !this.lastName.trim() || !this.email.trim() || !this.password.trim()) {
      this.status = 'invalid';
      return;
    }
    if (!this.isValidUsername(username)) {
      this.status = 'invalidUsername';
      return;
    }
    if (!this.meetsPasswordPolicy(this.password)) {
      this.status = 'weakPassword';
      return;
    }

    this.isSubmitting = true;
    this.status = 'idle';
    try {
      await this.api.register({
        username,
        firstName: this.firstName.trim(),
        lastName: this.lastName.trim(),
        email: this.email.trim(),
        password: this.password,
      });
      this.status = 'pending';
    } catch (error) {
      console.warn('Account registration failed.', error);
      this.status = 'error';
    } finally {
      this.isSubmitting = false;
    }
  }

  async confirmAccount(): Promise<void> {
    if (this.isConfirming) return;
    if (!this.email.trim() || !this.verificationCode.trim()) {
      this.status = 'invalid';
      return;
    }

    this.isConfirming = true;
    this.status = 'confirming';
    try {
      await this.api.confirmRegistration(this.email.trim(), this.verificationCode.trim());
      this.status = 'confirmed';
      await this.router.navigate(['/login']);
    } catch (error) {
      console.warn('Account confirmation failed.', error);
      this.status = 'confirmationError';
    } finally {
      this.isConfirming = false;
    }
  }

  private meetsPasswordPolicy(password: string): boolean {
    return password.length >= 12
      && /[a-z]/.test(password)
      && /[A-Z]/.test(password)
      && /\d/.test(password)
      && /[^A-Za-z0-9]/.test(password);
  }

  private isValidUsername(username: string): boolean {
    return username.length >= 3 && username.length <= 30 && /^[a-z0-9_.]+$/.test(username);
  }
}
